/**
 * User Statistics Redux Slice
 * Manages user statistics state for superadmin dashboard
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

// Async thunk for fetching user statistics with pagination and search
export const fetchUserStats = createAsyncThunk(
  'userStats/fetchUserStats',
  async ({ page = 1, limit = 5, search = '', sortBy = 'totalContacts', sortOrder = 'DESC' } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder
      });
      
      if (search) {
        params.append('search', search);
      }
      
      const response = await api.get(`/user-stats?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch user statistics'
      );
    }
  }
);

// Async thunk for fetching detailed user statistics
export const fetchUserDetailedStats = createAsyncThunk(
  'userStats/fetchUserDetailedStats',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/user-stats/user/${userId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch detailed user statistics'
      );
    }
  }
);

// Async thunk for fetching current user's own statistics
export const fetchMyStats = createAsyncThunk(
  'userStats/fetchMyStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/user-stats/me');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch your statistics'
      );
    }
  }
);

const initialState = {
  // User statistics data
  users: [],
  summary: {
    totalUsers: 0,
    activeUsers: 0,
    totalContactsCreated: 0,
    usersWithContacts: 0,
    topContributor: null
  },
  
  // Pagination
  pagination: {
    page: 1,
    limit: 5,
    total: 0,
    pages: 0
  },
  
  // Search and filters
  searchQuery: '',
  sortBy: 'totalContacts',
  sortOrder: 'DESC',
  
  // Detailed user stats (for modal/detailed view)
  selectedUser: null,
  selectedUserStats: null,
  
  // Current user's own stats
  myStats: null,
  myStatsLoading: false,
  myStatsError: null,
  
  // Loading states
  loading: false,
  detailedLoading: false,
  
  // Error states
  error: null,
  detailedError: null,
  
  // Last updated timestamp
  lastUpdated: null
};

const userStatsSlice = createSlice({
  name: 'userStats',
  initialState,
  reducers: {
    // Clear error messages
    clearError: (state) => {
      state.error = null;
      state.detailedError = null;
      state.myStatsError = null;
    },
    
    // Clear detailed user stats
    clearDetailedStats: (state) => {
      state.selectedUser = null;
      state.selectedUserStats = null;
      state.detailedError = null;
    },
    
    // Set selected user for detailed view
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },
    
    // Set search query
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
      state.pagination.page = 1; // Reset to first page on search
    },
    
    // Set sort options
    setSortOptions: (state, action) => {
      state.sortBy = action.payload.sortBy;
      state.sortOrder = action.payload.sortOrder;
    },
    
    // Set page
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch user statistics
      .addCase(fetchUserStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserStats.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.data.users;
        state.summary = action.payload.data.summary;
        state.pagination = action.payload.data.pagination;
        state.lastUpdated = new Date().toISOString();
        state.error = null;
      })
      .addCase(fetchUserStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch detailed user statistics
      .addCase(fetchUserDetailedStats.pending, (state) => {
        state.detailedLoading = true;
        state.detailedError = null;
      })
      .addCase(fetchUserDetailedStats.fulfilled, (state, action) => {
        state.detailedLoading = false;
        state.selectedUserStats = action.payload.data;
        state.detailedError = null;
      })
      .addCase(fetchUserDetailedStats.rejected, (state, action) => {
        state.detailedLoading = false;
        state.detailedError = action.payload;
      })
      
      // Fetch current user's own statistics
      .addCase(fetchMyStats.pending, (state) => {
        state.myStatsLoading = true;
        state.myStatsError = null;
      })
      .addCase(fetchMyStats.fulfilled, (state, action) => {
        state.myStatsLoading = false;
        state.myStats = action.payload.data;
        state.myStatsError = null;
      })
      .addCase(fetchMyStats.rejected, (state, action) => {
        state.myStatsLoading = false;
        state.myStatsError = action.payload;
      });
  }
});

// Export actions
export const { clearError, clearDetailedStats, setSelectedUser, setSearchQuery, setSortOptions, setPage } = userStatsSlice.actions;

// Export selectors
export const selectUserStats = (state) => state.userStats;
export const selectUserStatsLoading = (state) => state.userStats.loading;
export const selectUserStatsError = (state) => state.userStats.error;
export const selectUserStatsSummary = (state) => state.userStats.summary;
export const selectUsers = (state) => state.userStats.users;
export const selectPagination = (state) => state.userStats.pagination;
export const selectSearchQuery = (state) => state.userStats.searchQuery;
export const selectSortOptions = (state) => ({ sortBy: state.userStats.sortBy, sortOrder: state.userStats.sortOrder });
export const selectMyStats = (state) => state.userStats.myStats;
export const selectMyStatsLoading = (state) => state.userStats.myStatsLoading;
export const selectMyStatsError = (state) => state.userStats.myStatsError;

// Export reducer
export default userStatsSlice.reducer;